import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import { Dropdown, EmptyState, HubTable, Icon, TableActionsMenu, TableSettingsDialog } from "@/ds";
import { hubTableCartao, HubTableCartao } from "@/shared/components/hub-table-cartao";
import {
  MOEDAS_ACOES_KEY,
  MOEDAS_ACOES_WIDTH_REM,
  MOEDAS_COLUNAS,
  MOEDAS_DESCRICAO,
  MOEDAS_FILTRO_MOEDA_ROTULO,
  MOEDAS_FILTRO_SITUACAO_ROTULO,
  MOEDAS_LOADING_LABEL,
  MOEDAS_NOVA_COTACAO_LABEL,
  MOEDAS_ROWS_PER_PAGE_LABEL,
  MOEDAS_TITULO,
  MOEDAS_VAZIO_DESCRICAO,
  MOEDAS_VAZIO_TITULO,
} from "./moedas.constants";
import type { MoedasColumnKey } from "./moedas.constants";
import styles from "./moedas.module.scss";
import { NovaCotacaoDialog } from "./nova-cotacao-dialog";
import { SituacaoCotacaoDialog } from "./situacao-cotacao-dialog";
import type { MoedaLinha, MoedasViewModel } from "./types-moedas";
import type { OrdenarCotacoesReferenciaPor } from "@/modules/financeiro-configuracoes";

function renderCelula(chave: MoedasColumnKey, linha: MoedaLinha) {
  switch (chave) {
    case "competencia":
      return (
        <span className={styles.competencia}>
          <span className={styles.competenciaValor}>{linha.competencia}</span>
          {linha.tags.map((tag) => (
            <Tag key={tag.rotulo} value={tag.rotulo} severity={tag.tom} />
          ))}
        </span>
      );
    case "moeda":
      return linha.moeda;
    case "cotacao":
      return linha.taxa;
    case "cadastradaEm":
      return (
        <span className={styles.cadastrada}>
          <span className={styles.cadastradaData}>{linha.cadastradaEm}</span>
          <span className={styles.cadastradaPor}>{linha.cadastradaPor}</span>
        </span>
      );
    default:
      return linha.acao ? (
        <Button
          type="button"
          className={hubTableCartao.actionButtonClassName}
          icon={<Icon name="power-off" size={14} />}
          aria-label={linha.acao.label}
          aria-haspopup="dialog"
          onClick={linha.acao.executar}
          data-testid={linha.acao.testId}
          severity="secondary"
          text
        />
      ) : null;
  }
}

export function MoedasView({
  filtroMoeda,
  mostrarFiltroMoeda,
  filtroSituacao,
  novaCotacao,
  menu,
  configuracoes,
  tabela,
  dialogo,
  situacaoDialogo,
}: MoedasViewModel) {
  return (
    <section className={styles.tela} aria-label="Moedas" data-testid="financeiro-moedas-screen">
      <div className={styles.barra}>
        <div className={styles.filtros}>
          {mostrarFiltroMoeda ? (
            <Dropdown
              className={styles.filtro}
              size="sm"
              value={filtroMoeda.value}
              options={filtroMoeda.options}
              optionLabel="label"
              optionValue="value"
              ariaLabel={MOEDAS_FILTRO_MOEDA_ROTULO}
              showClear={false}
              valueTemplate={(opcao) => `${MOEDAS_FILTRO_MOEDA_ROTULO}: ${opcao?.label ?? ""}`}
              onChange={(evento) => filtroMoeda.onChange(evento.value)}
              data-testid="moedas-filtro-moeda"
            />
          ) : null}
          <Dropdown
            className={styles.filtro}
            size="sm"
            value={filtroSituacao.value}
            options={filtroSituacao.options}
            optionLabel="label"
            optionValue="value"
            ariaLabel={MOEDAS_FILTRO_SITUACAO_ROTULO}
            showClear={false}
            valueTemplate={(opcao) => `${MOEDAS_FILTRO_SITUACAO_ROTULO}: ${opcao?.label ?? ""}`}
            onChange={(evento) => filtroSituacao.onChange(evento.value)}
            data-testid="moedas-filtro-situacao"
          />
        </div>
        <div className={styles.acoes}>
          {novaCotacao ? (
            <Button
              type="button"
              icon={<Icon name="plus" size={14} />}
              label={MOEDAS_NOVA_COTACAO_LABEL}
              onClick={novaCotacao}
              data-testid="moedas-nova-cotacao"
            />
          ) : null}
          <TableActionsMenu.Root testId="moedas-menu">
            <TableActionsMenu.Trigger />
            <TableActionsMenu.ExportItem
              onClick={menu.exportar}
              disabled={menu.exportDesabilitado}
              title={menu.exportMotivo}
            />
            <TableActionsMenu.SettingsItem onClick={menu.abrirConfiguracoes} />
          </TableActionsMenu.Root>
        </div>
      </div>

      <header className={styles.secao}>
        <h2 className={styles.titulo}>{MOEDAS_TITULO}</h2>
        <p className={styles.descricao}>{MOEDAS_DESCRICAO}</p>
      </header>

      <HubTableCartao<MoedaLinha, MoedasColumnKey, OrdenarCotacoesReferenciaPor>
        value={tabela.items}
        totalRecords={tabela.total}
        table={tabela.table}
        settings={tabela.settings}
        status={tabela.status}
        errorMessage={tabela.errorMessage}
        loadingLabel={MOEDAS_LOADING_LABEL}
        emptyNoun="cotação"
        emptyGender="f"
        hasFilters={tabela.hasFilters}
        hasSearch={false}
        onClear={tabela.onClear}
        onRetry={tabela.reload}
        rowClassName={(linha) => (linha.inativa ? styles.linhaInativa : undefined)}
        dataTableProps={tabela.dataTableProps}
        rowsPerPageLabel={MOEDAS_ROWS_PER_PAGE_LABEL}
        testId="moedas"
        data-testid="moedas-table-wrapper"
        data-first-load={tabela.isFirstLoad || undefined}
      >
        {MOEDAS_COLUNAS.map((coluna) => (
          <HubTable.Column<MoedaLinha, MoedasColumnKey>
            key={coluna.key}
            field={coluna.key}
            columnKey={coluna.key}
            header={coluna.header}
            sortable
            align={coluna.align}
            alignHeader={coluna.align}
            style={
              coluna.widthRem
                ? { width: `${coluna.widthRem}rem`, minWidth: `${coluna.widthRem}rem` }
                : undefined
            }
            body={(linha) => renderCelula(coluna.key, linha)}
          />
        ))}
        {tabela.mostrarAcoes ? (
          <HubTable.Column<MoedaLinha, MoedasColumnKey>
            field={MOEDAS_ACOES_KEY}
            columnKey={MOEDAS_ACOES_KEY}
            header=""
            align="center"
            bodyClassName={hubTableCartao.actionCellClassName}
            headerClassName={hubTableCartao.actionCellClassName}
            style={{
              width: `${MOEDAS_ACOES_WIDTH_REM}rem`,
              minWidth: `${MOEDAS_ACOES_WIDTH_REM}rem`,
            }}
            body={(linha) => renderCelula(MOEDAS_ACOES_KEY, linha)}
          />
        ) : null}
        {tabela.semCotacoes ? (
          <HubTable.Empty>
            <EmptyState.Root size="sm" testId="moedas-empty">
              <EmptyState.Illustration name="sem-dados" />
              <EmptyState.Title>{MOEDAS_VAZIO_TITULO}</EmptyState.Title>
              <EmptyState.Description>{MOEDAS_VAZIO_DESCRICAO}</EmptyState.Description>
              {tabela.novaCotacao ? (
                <EmptyState.Action
                  label={MOEDAS_NOVA_COTACAO_LABEL}
                  icon="plus"
                  severity={undefined}
                  onClick={tabela.novaCotacao}
                  data-testid="moedas-empty-nova-cotacao"
                />
              ) : null}
            </EmptyState.Root>
          </HubTable.Empty>
        ) : null}
      </HubTableCartao>

      <TableSettingsDialog
        dialog={configuracoes.dialog}
        testId="moedas-table-settings"
        onApplied={configuracoes.onApplied}
      />

      {dialogo ? <NovaCotacaoDialog {...dialogo} /> : null}
      {situacaoDialogo ? <SituacaoCotacaoDialog {...situacaoDialogo} /> : null}
    </section>
  );
}
